/**
 * JobProgressCard - Affiche la progression d'un job de génération
 * Extrait de CreateArticle.js pour améliorer la maintenabilité
 */
import React from 'react';
import { Loader2, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/**
 * Formate le temps écoulé en MM:SS
 */
export const formatElapsedTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Indicateur de heartbeat (polling actif)
 */
export const HeartbeatIndicator = ({ lastPollTime }) => (
  <div 
    className={`w-2 h-2 rounded-full transition-all duration-300 ${
      lastPollTime && (Date.now() - lastPollTime) < 3000 
        ? 'bg-emerald-500 animate-pulse' 
        : 'bg-amber-500'
    }`}
    title={lastPollTime ? `Dernière MAJ: il y a ${Math.floor((Date.now() - lastPollTime) / 1000)}s` : 'En attente...'}
  />
);

/**
 * Étape du pipeline V2 (Compendium ou Angles)
 */
export const PipelineStep = ({ 
  isActive, 
  isComplete, 
  icon, 
  label, 
  stats, 
  activeColor = 'purple',
  activeText = 'En cours...'
}) => {
  const colorClasses = {
    purple: {
      active: 'bg-purple-500/20 ring-1 ring-purple-500/50',
      text: 'text-purple-300',
      loader: 'text-purple-400',
      subtext: 'text-purple-400'
    },
    amber: {
      active: 'bg-amber-500/20 ring-1 ring-amber-500/50',
      text: 'text-amber-300',
      loader: 'text-amber-400',
      subtext: 'text-amber-400'
    }
  };
  
  const colors = colorClasses[activeColor] || colorClasses.purple;
  
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
      isActive ? colors.active :
      isComplete ? 'bg-green-500/10' : 'bg-slate-800/50'
    }`}>
      {isActive ? (
        <Loader2 className={`w-4 h-4 ${colors.loader} animate-spin`} />
      ) : isComplete ? (
        <CheckCircle className="w-4 h-4 text-green-400" />
      ) : (
        <Clock className="w-4 h-4 text-slate-500" />
      )}
      <div>
        <p className={`text-xs font-medium ${
          isActive ? colors.text :
          isComplete ? 'text-green-400' : 'text-slate-500'
        }`}>
          {icon} {label}
        </p>
        {isComplete && stats && (
          <p className="text-[10px] text-slate-500">{stats}</p>
        )}
        {isActive && (
          <p className={`text-[10px] ${colors.subtext}`}>{activeText}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Indicateur de statut d'un site
 */
export const SiteStatusIndicator = ({ status, siteName, progress, error }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'generating_text':
      case 'generating_image':
      case 'publishing':
        return <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'completed':
        return 'Terminé';
      case 'error':
        return error || 'Erreur';
      case 'generating_text':
        return 'Rédaction...';
      case 'generating_image':
        return 'Image...';
      case 'publishing':
        return 'Publication...';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  return (
    <div className={`flex items-center justify-between p-2 rounded-lg ${
      status === 'completed' ? 'bg-green-500/10' :
      status === 'error' ? 'bg-red-500/10' :
      status === 'pending' ? 'bg-slate-800/50' :
      'bg-cyan-500/10'
    }`}>
      <div className="flex items-center gap-2">
        {getStatusIcon()}
        <span className={`text-sm ${
          status === 'completed' ? 'text-green-400' :
          status === 'error' ? 'text-red-400' :
          status === 'pending' ? 'text-slate-500' :
          'text-cyan-400'
        }`}>
          {siteName}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {progress > 0 && progress < 100 && (
          <span className="text-xs text-slate-500">{progress}%</span>
        )}
        <span className={`text-xs ${
          status === 'error' ? 'text-red-400' : 'text-slate-500'
        }`}>
          {getStatusText()}
        </span>
      </div>
    </div>
  );
};

/**
 * Messages dynamiques pendant la génération
 */
const PROGRESS_MESSAGES = [
  "Analyse des sources en cours...",
  "Extraction des informations clés...",
  "Rédaction de l'article...",
  "Optimisation SEO...",
  "Génération de l'image...",
  "Finalisation..."
];

export const DynamicProgressMessage = ({ elapsedTime }) => {
  const messageIndex = Math.min(
    Math.floor(elapsedTime / 15),
    PROGRESS_MESSAGES.length - 1
  );
  
  return (
    <p className="text-xs text-slate-500 italic animate-pulse">
      {PROGRESS_MESSAGES[messageIndex]}
    </p>
  );
};

/**
 * Composant principal de progression du job
 */
const JobProgressCard = ({
  jobStatus,
  elapsedTime,
  lastPollTime,
  sites = []
}) => {
  if (!jobStatus) return null;

  const getProgressTitle = () => {
    if (jobStatus.pipeline_step === 'compendium') return 'Analyse des sources...';
    if (jobStatus.pipeline_step === 'angles') return 'Définition des angles...';
    return `Génération en cours (${jobStatus.completed_sites}/${jobStatus.total_sites})`;
  };

  return (
    <Card className="bg-slate-900/50 border-cyan-500/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {getProgressTitle()}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-slate-400 tabular-nums">{formatElapsedTime(elapsedTime)}</span>
            </div>
            <HeartbeatIndicator lastPollTime={lastPollTime} />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Pipeline V2 : Étapes préparatoires */}
        {jobStatus.pipeline_v2 && (
          <div className="mb-4 pb-4 border-b border-slate-800">
            <p className="text-xs text-slate-500 uppercase mb-3 font-mono">Pipeline V2 — Préparation</p>
            <div className="grid grid-cols-2 gap-3">
              <PipelineStep
                isActive={jobStatus.pipeline_step === 'compendium'}
                isComplete={!!jobStatus.compendium_stats}
                icon="📚"
                label="Compendium"
                stats={jobStatus.compendium_stats ? `${jobStatus.compendium_stats.facts} faits, ${jobStatus.compendium_stats.figures} chiffres` : null}
                activeColor="purple"
                activeText="Analyse en cours..."
              />
              <PipelineStep
                isActive={jobStatus.pipeline_step === 'angles'}
                isComplete={!!jobStatus.angles_count}
                icon="🎯"
                label="Angles uniques"
                stats={jobStatus.angles_count ? `${jobStatus.angles_count} angle${jobStatus.angles_count > 1 ? 's' : ''} défini${jobStatus.angles_count > 1 ? 's' : ''}` : null}
                activeColor="amber"
                activeText="Personnalisation..."
              />
            </div>
          </div>
        )}

        {/* Liste des sites */}
        {jobStatus.sites_status && jobStatus.sites_status.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500 uppercase mb-2 font-mono">Sites</p>
            {jobStatus.sites_status.map((siteStatus, idx) => (
              <SiteStatusIndicator
                key={siteStatus.site_id || idx}
                status={siteStatus.status}
                siteName={siteStatus.site_name || `Site ${idx + 1}`}
                progress={siteStatus.progress}
                error={siteStatus.error}
              />
            ))}
          </div>
        )}

        {/* Message dynamique */}
        <div className="mt-4 pt-3 border-t border-slate-800">
          <DynamicProgressMessage elapsedTime={elapsedTime} />
        </div>
      </CardContent>
    </Card>
  );
};

export default JobProgressCard;
